/**
 * utils/pvEchecProcessor.js
 * Processeur principal pour traiter les données des PV d'échec
 */

import { SorProcessorUtils } from "./sorProcessorUtils.js";
import { DirectoryScanner } from "./directoryScanner.js";
import { SorFailureManager } from "./sorFailureManager.js";
import { SorMetadataExtractor } from "./sorMetadataExtractor.js";

export class PVEchecProcessor {
  /**
   * Traiter un répertoire sélectionné pour les PV d'échec
   * @param {FileSystemDirectoryHandle} directoryHandle - Handle du répertoire
   * @param {Object} [options] - Options de traitement
   * @param {boolean} [options.allowEmpty=false] - Si true, accepte les dossiers vides
   */
  static async processDirectory(directoryHandle, options = {}) {
    const { allowEmpty = false } = options;

    try {
      // Scanner le répertoire avec l'option allowEmpty
      const scanResult = await DirectoryScanner.scanDirectory(directoryHandle, { allowEmpty });

      if (!scanResult.isValid) {
        return scanResult;
      }

      // Si le dossier est vide et autorisé, retourner un résultat vide valide
      if (scanResult.data.files.length === 0 && allowEmpty) {
        return {
          isValid: true,
          data: { fibers: [] },
          errors: [],
        };
      }

      // 🆕 Enrichir tous les fichiers avec métadonnées des fichier sor
      for (const file of scanResult.data.files) {
        await SorMetadataExtractor.enrichFileObject(file);
      }
      // Grouper les fichiers avec gestion des tentatives
      const grouped = SorFailureManager.groupFailureFiles(scanResult.data.files);
      // Valider chaque fibre
      this._validateFibers(grouped);

      // Convertir en array simple pour l'app state
      const fibers = this._convertToArray(grouped);
      return {
        isValid: true,
        data: { fibers },
        errors: [],
      };
    } catch (error) {
      return {
        isValid: false,
        errors: [`Erreur: ${error.message}`],
        data: null,
      };
    }
  }

  /**
   * Convertir la structure groupée en array simple
   */
  static _convertToArray(grouped) {
    const fibers = [];

    Object.keys(grouped).forEach((tiroir) => {
      Object.keys(grouped[tiroir]).forEach((module) => {
        Object.values(grouped[tiroir][module]).forEach((fiber, index) => {
          try {
            const moduleCode = SorProcessorUtils.extractModuleCode(module);
            const REFERENCE = SorProcessorUtils.buildReference(tiroir, moduleCode);
            const bpoFile = SorProcessorUtils.getFirstFromSet(fiber.pboValues);

            fibers.push({
              REFERENCE,
              tiroir,
              module: moduleCode,
              port: fiber.port,
              pboFolder: null,
              bpoFile,
              nm1310: !!fiber.measurementGroups[1310],
              nm1550: !!fiber.measurementGroups[1550],
              files: fiber.allAttempts,
            });
          } catch (error) {
            SorProcessorUtils.logFiberError(error, "pvEchec", index, tiroir, module, {
              port: fiber?.port,
              pboValues: fiber?.pboValues ? Array.from(fiber.pboValues) : [],
              measurementGroups: fiber?.measurementGroups ? Object.keys(fiber.measurementGroups) : [],
              allAttempts: fiber?.allAttempts?.length || 0,
            });
            throw error; // Relancer l'erreur pour arrêter le processus
          }
        });
      });
    });

    return SorProcessorUtils.sortFibers(fibers);
  }

  /**
   * Valider toutes les fibres avec détection des échecs
   */
  static _validateFibers(grouped) {
    Object.values(grouped).forEach((modules) => {
      Object.values(modules).forEach((fibres) => {
        Object.values(fibres).forEach((fiber) => {
          // Validation PBO (cohérence dans les tentatives)
          fiber.pboValidation = SorFailureManager.validateFailurePBO(fiber.pboValues);

          // Validation des tentatives
          fiber.attemptValidation = SorFailureManager.validateFiberAttempts(fiber);

          // Status global de la fibre
          fiber.isValid = fiber.attemptValidation.isValid && fiber.pboValidation.isValid;
          fiber.status = fiber.isValid ? "ok" : "failure";
        });
      });
    });
  }
}
